import { Component, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { DirectReporteeSchedule } from '@app/modules/generate-schedules/models/generate-schedules.model';
import { GenerateScheduleService } from '@app/modules/generate-schedules/services/generate-schedule.service';
import { TranslateService } from '@ngx-translate/core';
import { DateChangeEvent, SchedulerEvent } from '@progress/kendo-angular-scheduler';
import moment from 'moment';
import { finalize } from 'rxjs/operators';
import { WorkflowsAbsencesService } from '@app/core/services/workflows-absences/workflows-absences.service';
import { TimeOffInLieuService } from '@app/core/services/time-off-in-lieu/time-off-in-lieu.service';
import { ToilClassType } from "@app/shared/models/time-off-in-lieu/time-off-in-lieu.model";
import { from } from "rxjs";
import { ToilListVariant } from "@app/modules/lookup/models/toil-list-variant.model";
import { modules } from '@app/consts/modules';
import { features } from '@app/consts';
import { ModuleConfigurationService } from '@app/core/services/module-configuration/module-configuration.service';
import { FeatureService } from '@app/core/services/feature.service';

const displayDate = moment().toDate();
const currentYear = new Date().getFullYear();

const randomInt = (min, max): number => {
    return Math.floor(Math.random() * (max - min + 1)) + min;
}

const parseAdjust = (eventDate: string): Date => {
    // Remove timezone from date string
    // const date = new Date( eventDate.substring(0, 19) );
    const date = new Date(eventDate);

    return date;
};

const parseAdjustDay = (eventDate: string): Date => {
    if (eventDate === null || eventDate === undefined) {
        return null;
    }
    // Remove timezone from date string
    const date = new Date(eventDate.substring(0, 19));

    return date;
};

const parseAdjustDayAddDays = (eventDate: string, numberOfDays: number): Date => {
    if (eventDate === null || eventDate === undefined) {
        return null;
    }
    if (numberOfDays === null || numberOfDays === undefined) {
        numberOfDays = 0;
    }
    // Remove timezone from date string
    let date = new Date(eventDate.substring(0, 19));

    // Add a day to the date
    date.setDate(date.getDate() + numberOfDays);

    return date;
};

const getTimeZone = (eventDate: string): string => {
    if (eventDate === null || eventDate === undefined) {
        return '';
    }
    // Remove timezone from date string

    return eventDate.substring(20, 25);
};

@Component({
    selector: 'app-direct-reportees-schedule',
    templateUrl: './direct-reportees-schedule.component.html',
    styleUrls: ['./direct-reportees-schedule.component.scss']
})
export class DirectReporteesScheduleComponent implements OnInit {
    public modules: typeof modules = modules;
    public features: typeof features = features;
    public selectedDate: Date = displayDate;
    from: string = moment(this.selectedDate).subtract(1, 'months').startOf('month').format('YYYY-MM-DD');
    to: string = moment(this.selectedDate).add(1, 'months').endOf('month').format('YYYY-MM-DD');
    isLoading: boolean = true;
    scheduleData: any[] = [];

    eventsToDisplayOptions = [
        {
            id: 'schedule',
            text: 'Schedule'
        },
        {
            id: 'timeOff',
            text: 'TimeOff'
        },
        {
            id: 'toil',
            text: 'TOIL'
        },
    ]

    timeOffStatus = [
        {
            id: 'approved',
            text: 'AbsenceRequestApproved'
        },
        {
            id: 'pending',
            text: 'AbsenceRequestPending'
        },
    ];

    toilStatus = [
        {
            id: 'trs_Approved',
            text: 'Approved'
        },
        {
            id: 'trs_Pending',
            text: 'Pending'
        },
        {
            id: 'trs_Denied',
            text: 'Denied'
        },
        {
            id: 'trs_Cancelled',
            text: 'Cancelled'
        },
    ];

    toilClassTypes: ToilClassType[] = [];
    toilTypeList: ToilListVariant[] = [];

    scheduleFiltersForm;
    schedule: DirectReporteeSchedule[];
    filteredScheduleData: any[] = [];
    timeOffTypes: { id: string, name: string }[] = [];
    hasToilModuleAccess: boolean = false;
    hasToilFeatureAccess: boolean = false;

    constructor(
        private fb: FormBuilder,
        public translate: TranslateService,
        private generateScheduleService: GenerateScheduleService,
        private workflowsAbsencesService: WorkflowsAbsencesService,
        private timeOffInLieuService: TimeOffInLieuService,
        private moduleConfigurationService: ModuleConfigurationService,
        private featureService: FeatureService
    ) {
        this.hasToilModuleAccess = this.moduleConfigurationService.hasModuleAccess(modules.TOIL);
        this.hasToilFeatureAccess = this.featureService.hasFeatureAccess(features.TOIL);

        this.scheduleFiltersForm = this.fb.group({
            employees: [['all']],
            eventsToDisplay: [['all']],
            timeOffType: [['all']],
            timeOffStatus: [['all']],
            toilClassType: [['all']],
            toilType: [['all']],
            toilStatus: [['all']],
        });

        this.scheduleFiltersForm.valueChanges
            .subscribe(value => {
                this.filterScheduleData();
            });
    }

    ngOnInit(): void {
        if(this.hasToilModuleAccess && this.hasToilFeatureAccess) {
            this.getToilClassTypes(0, 100);
            this.getToilTypes();
        }
        this.getData();
    }

    dateChange(event: DateChangeEvent) {
        if (!this.isLoading) {
            this.selectedDate = event.selectedDate;
            this.from = moment(this.selectedDate).subtract(1, 'months').startOf('month').format('YYYY-MM-DD');
            this.to = moment(this.selectedDate).add(1, 'months').endOf('month').format('YYYY-MM-DD');
            this.getData();
        }
    }

    getToilClassTypes(skip: number, take: number) {
        this.timeOffInLieuService.getToilClassTypes(take, skip).subscribe(
            pagedData => {
                this.toilClassTypes.push(...pagedData.data);

                if (take < pagedData.total) {
                    this.getToilClassTypes(skip + 100, take + 100);
                }
            });
    }

    private getToilTypes() {
        from(this.timeOffInLieuService.getAllToilTypes('name-asc'))
            .subscribe(
                timeOffTypePager => {
                    this.toilTypeList = timeOffTypePager.data;
                }
            );
    }

    getData() {
        this.isLoading = true;

        // Fetch both schedule and pending time off data
        this.generateScheduleService.getDirectReporteeSchedules(this.from, this.to)
            .pipe(
                finalize(() => {
                    this.isLoading = false;
                })
            )
            .subscribe(
                res => {
                    this.schedule = res;

                    // Initialize toilRequests and timeOffRequests arrays for each employee schedule
                    this.schedule.forEach(item => {
                        if (!item.toilRequests) {
                            item.toilRequests = [];
                        }
                        if (!item.timeOffRequests) {
                            item.timeOffRequests = [];
                        }
                    });

                    this.sortAlphabeticallyByEmployeeFirstName();
                    this.getTimeOffTypes();

                    // Initialize filteredScheduleData with basic schedule
                    this.filteredScheduleData = this.formatData(
                        this.filterTimeOffTypes(
                            this.filterOutEmployeesSchedule(this.schedule)
                        )
                    );

                    // Fetch pending time off data
                    this.workflowsAbsencesService.getPendingApproval()
                        .subscribe(pendingData => {
                            // Add pending time off data to the schedule
                            if (pendingData && pendingData.data) {
                                pendingData.data.forEach(pendingItem => {
                                    const employeeSchedule = this.schedule.find(s =>
                                        s.employee.firstName === pendingItem.requestedBy.firstName &&
                                        s.employee.lastName === pendingItem.requestedBy.lastName
                                    );

                                    if (employeeSchedule) {
                                        employeeSchedule.timeOffRequests.push({
                                            id: pendingItem.id,
                                            start: pendingItem.from,
                                            end: pendingItem.to,
                                            type: pendingItem.timeOffType
                                        });
                                    }
                                });
                            }

                            // Fetch pending TOIL requests
                            if (this.hasToilModuleAccess && this.hasToilFeatureAccess) {
                                this.timeOffInLieuService.getDirectReporteesRequests(100, 0, 'version.createdOn-desc', null)
                                    .subscribe((toilData: any) => {
                                        if (toilData && toilData.data) {
                                            toilData.data.forEach(toilItem => {
                                                const employeeSchedule = this.schedule.find(s =>
                                                    s.employee.firstName === toilItem.employee.firstName &&
                                                    s.employee.lastName === toilItem.employee.lastName
                                                );
    
                                                if (employeeSchedule) {
                                                    employeeSchedule.toilRequests.push({
                                                        id: toilItem.id,
                                                        start: toilItem.from,
                                                        end: toilItem.to,
                                                        type: {
                                                            id: toilItem.toilPolicy.toilType.id,
                                                            name: toilItem.toilPolicy.toilType.name
                                                        },
                                                        toilClassType: toilItem.toilClassType,
                                                        requestStatus: toilItem.requestStatus,
                                                    });
                                                }
                                            });
                                        }
    
                                        // Update filteredScheduleData with all data
                                        this.filteredScheduleData = this.formatData(
                                            this.filterTimeOffTypes(
                                                this.filterOutEmployeesSchedule(this.schedule)
                                            )
                                        );
                                    });
                            }
                        });
                }
            );
    }

    sortAlphabeticallyByEmployeeFirstName() {
        this.schedule.sort((a, b) => a.employee.firstName.localeCompare(b.employee.firstName));
    }

    getTimeOffTypes() {
        if (!this.schedule || !Array.isArray(this.schedule)) {
            return;
        }

        this.schedule.forEach(item => {
            if (!item) return;

            if (Array.isArray(item.timeoffSchedule)) {
                item.timeoffSchedule.forEach(absence => {
                    if (absence?.type?.id && !this.timeOffTypes.some(a => a?.id === absence.type.id)) {
                        this.timeOffTypes.push(absence.type);
                    }
                });
            }

            if (Array.isArray(item.pendingRequests)) {
                item.pendingRequests.forEach(absence => {
                    if (absence?.type?.id && !this.timeOffTypes.some(a => a?.id === absence.type.id)) {
                        this.timeOffTypes.push(absence.type);
                    }
                });
            }
        });

        if (this.timeOffTypes.length > 0) {
            this.timeOffTypes.sort((a, b) => {
                if (!a?.name || !b?.name) return 0;
                return a.name.localeCompare(b.name);
            });
        }
    }

    filterScheduleData() {
        this.filteredScheduleData = this.formatData(
            this.filterTimeOffTypes(
                this.filterOutEmployeesSchedule(this.schedule)
            )
        );
    }

    formatData(schedule) {
        let s: SchedulerEvent[] = [];
        let selectedSectionsToDisplay = this.scheduleFiltersForm.get('eventsToDisplay').value;
        let selectedTimeOffStatus = this.scheduleFiltersForm.get('timeOffStatus').value;
        let selectedToilClassType = this.scheduleFiltersForm.get('toilClassType').value;
        let selectedToilType = this.scheduleFiltersForm.get('toilType').value;
        let selectedToilStatus = this.scheduleFiltersForm.get('toilStatus').value;

        if (selectedSectionsToDisplay === null) {
            selectedSectionsToDisplay = [];
        }

        schedule.forEach(
            item => {
                if (selectedSectionsToDisplay.includes('schedule') || selectedSectionsToDisplay.includes('all')) {
                    s.push(
                        item.workSchedule.map(
                            dataItem => (
                                <SchedulerEvent>{
                                    id: randomInt(1, 10),
                                    start: parseAdjust(dataItem.start),
                                    startTimezone: getTimeZone(dataItem.start),
                                    end: parseAdjust(dataItem.end),
                                    endTimezone: getTimeZone(dataItem.end),
                                    isAllDay: false,
                                    title: `${item.employee.firstName} ${item.employee.lastName} - Work Day`,
                                    description: 'SchedulerWorkDay',
                                    recurrenceRule: null,
                                    recurrenceId: null,
                                    recurrenceException: null,

                                    roomId: null,
                                    ownerID: null,
                                    type: 'SchedulerWorkDay',
                                }
                            )
                        )
                    );
                }

                if (selectedSectionsToDisplay.includes('timeOff') || selectedSectionsToDisplay.includes('all')) {

                    if (selectedTimeOffStatus.includes('approved') || selectedTimeOffStatus.includes('all')) {
                        s.push(item.timeoffSchedule.map(dataItem => (
                            <SchedulerEvent>{
                                id: randomInt(1, 10),
                                start: parseAdjustDay(dataItem.start),
                                startTimezone: null,
                                end: parseAdjustDayAddDays(dataItem.end, 1),
                                endTimezone: null,
                                isAllDay: false,
                                title: `${item.employee.firstName} ${item.employee.lastName} - ${dataItem.type.name}`,
                                description: 'SchedulerLeave',
                                recurrenceRule: null,
                                recurrenceId: null,
                                recurrenceException: null,

                                roomId: null,
                                ownerID: null,
                                type: 'SchedulerLeave',
                            }
                        )));
                    }

                    if (selectedTimeOffStatus.includes('pending') || selectedTimeOffStatus.includes('all')) {
                        s.push(item.pendingRequests.map(dataItem => (
                            <SchedulerEvent>{
                                id: randomInt(1, 10),
                                start: parseAdjustDay(dataItem.start),
                                startTimezone: null,
                                end: parseAdjustDayAddDays(dataItem.end, 1),
                                endTimezone: null,
                                isAllDay: false,
                                title: `${item.employee.firstName} ${item.employee.lastName} - ${dataItem.type?.name}`,
                                description: 'SchedulerPendingTimeOff',
                                recurrenceRule: null,
                                recurrenceId: null,
                                recurrenceException: null,

                                roomId: null,
                                ownerID: null,
                                type: 'SchedulerPendingTimeOff',
                            }
                        )));
                    }
                }

                if (this.hasToilModuleAccess && this.hasToilFeatureAccess) {
                    if (selectedSectionsToDisplay.includes('toil') || selectedSectionsToDisplay.includes('all')) {
    
                        if (selectedToilStatus.includes('all')) {
                            item.toilRequests.forEach(request => {
                                if(selectedToilClassType.includes('all')) {
                                    s.push(this.createToilSchedulerEvent(request, item));
                                }
                                else if (selectedToilClassType.some(type => type !== 'all')) {
                                    if (request.toilClassType && selectedToilClassType.includes(request.toilClassType.id)) {
                                        s.push(this.createToilSchedulerEvent(request, item));
                                    }
                                }
                            });
                        }
                        else if (selectedToilStatus.some(type => type !== 'all')) {
                            let toilRequests = item.toilRequests.filter(request =>
                                selectedToilStatus.includes(request.requestStatus.id)
                            );
    
                            toilRequests.forEach(request => {
                                if(selectedToilClassType.includes('all')) {
                                    s.push(this.createToilSchedulerEvent(request, item));
                                }
                                else if (selectedToilClassType.some(type => type !== 'all')) {
                                    if (request.toilClassType && selectedToilClassType.includes(request.toilClassType.id)) {
                                        s.push(this.createToilSchedulerEvent(request, item));
                                    }
                                }
                            });
                        }
                    }
                }
            }

        )

        return [...s.flat()]
    }

    filterOutEmployeesSchedule(schedule: DirectReporteeSchedule[]): any {
        let selectedEmployees = this.scheduleFiltersForm.get('employees').value;

        if (selectedEmployees === null) {
            selectedEmployees = [];
        }

        if (selectedEmployees.includes('all')) {
            return schedule;
        } else if (selectedEmployees.length === 0) {
            return [];
        } else {
            return schedule.filter(
                item => {
                    return selectedEmployees.includes(item.employee) ? item.employee : null;
                }
            )
        }
    }

    filterTimeOffTypes(schedule: DirectReporteeSchedule[]): any {
        let scheduleClone: DirectReporteeSchedule[] = JSON.parse(JSON.stringify(schedule))
        let timeOffTypes = this.scheduleFiltersForm.get('timeOffType').value;
        let selectedToilClassType = this.scheduleFiltersForm.get('toilClassType').value;
        let selectedToilType = this.scheduleFiltersForm.get('toilType').value;

        if (timeOffTypes === null) {
            timeOffTypes = [];
        }

        if (selectedToilClassType === null) {
            selectedToilClassType = [];
        }

        if (selectedToilType === null) {
            selectedToilType = [];
        }

        if (timeOffTypes.includes('all')) {
            // Filter TOIL requests based on TOIL Class Type
            if (!selectedToilClassType.includes('all')) {
                scheduleClone.forEach(item => {
                    if (item.toilRequests) {
                        item.toilRequests = item.toilRequests.filter(request =>
                            request.toilClassType && selectedToilClassType.includes(request.toilClassType.id)
                        );
                    }
                });
            }

            // Filter TOIL requests based on TOIL Type
            if (!selectedToilType.includes('all')) {
                scheduleClone.forEach(item => {
                    if (item.toilRequests) {
                        item.toilRequests = item.toilRequests.filter(request =>
                            selectedToilType.includes(request.type.id)
                        );
                    }
                });
            }

            return scheduleClone;
        } else if (timeOffTypes.length === 0) {
            return [];
        } else {
            scheduleClone.forEach(item => {
                if (item.timeoffSchedule) {
                    item.timeoffSchedule = item.timeoffSchedule.filter(
                        timeOff => timeOffTypes.includes(timeOff.type.id)
                    );
                }
            });

            // Filter TOIL requests based on TOIL Class Type
            if (!selectedToilClassType.includes('all')) {
                scheduleClone.forEach(item => {
                    if (item.toilRequests) {
                        item.toilRequests = item.toilRequests.filter(request =>
                            request.toilClassType && selectedToilClassType.includes(request.toilClassType.id)
                        );
                    }
                });
            }

            // Filter TOIL requests based on TOIL Type
            if (!selectedToilType.includes('all')) {
                scheduleClone.forEach(item => {
                    if (item.toilRequests) {
                        item.toilRequests = item.toilRequests.filter(request =>
                            selectedToilType.includes(request.type.id)
                        );
                    }
                });
            }

            return scheduleClone;
        }
    }

    createToilSchedulerEvent(toilRequest: any, employeeDetails: any): SchedulerEvent {
        return <SchedulerEvent>{
            id: randomInt(1, 10),
            start: parseAdjustDay(toilRequest.start),
            startTimezone: null,
            end: parseAdjustDayAddDays(toilRequest.end, 1),
            endTimezone: null,
            isAllDay: false,
            title: `${employeeDetails.employee.firstName} ${employeeDetails.employee.lastName} - ${toilRequest.type.name}`,
            description: `Scheduler${toilRequest.toilClassType?.id}-${toilRequest.requestStatus?.id}`,
            recurrenceRule: null,
            recurrenceId: null,
            recurrenceException: null,
            roomId: null,
            ownerID: null,
            type: `Scheduler${toilRequest.toilClassType?.id}-${toilRequest.requestStatus?.id}`,
        };
    }
}
